"use client";

import { useEffect, useRef } from "react";
import { Monitor } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { apiClient } from "@/lib/api-client";

const TOAST_ID = "xynth-mobile-hint";
const TOAST_DURATION_MS = 90_000;

const dismissedStorageKey = (uid: string) => `xynth:mobile-hint-dismissed:${uid}`;

function MobileHintToastCard({ onGotIt }: { onGotIt: () => void | Promise<void> }) {
  return (
    <div className="pointer-events-auto mx-auto flex w-[calc(100vw-32px)] max-w-sm items-center gap-2.5 rounded-xl border border-white/10 bg-zinc-950/95 px-3 py-2.5 text-zinc-100 shadow-lg backdrop-blur-md">
      <Monitor className="size-4 shrink-0 text-blue-300" />
      <p className="min-w-0 flex-1 text-xs leading-snug text-zinc-300 line-clamp-2">
        <span className="font-medium text-zinc-100">Desktop recommended</span>
        {" — notebooks & deep research work best on a bigger screen."}
      </p>
      <button
        type="button"
        onClick={() => void onGotIt()}
        className="shrink-0 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-zinc-950 active:opacity-90"
      >
        Got it
      </button>
    </div>
  );
}

export function MobileHintToast() {
  const { user, loading } = useAuth();
  /** Only one prompt per tab load — pathname is intentionally omitted so chat navigation does not re-open the toast. */
  const ranForUserRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (loading) return;

    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    if (!isMobile) return;
    if (!user?.uid) return;

    if (ranForUserRef.current === user.uid) return;
    ranForUserRef.current = user.uid;

    try {
      if (localStorage.getItem(dismissedStorageKey(user.uid)) === "1") return;
    } catch {
      /* ignore */
    }

    let cancelled = false;
    let timer: number | null = null;

    const showHint = async () => {
      try {
        const { should_show } = await apiClient.getMobileHintStatus();
        if (cancelled || !should_show) return;

        timer = window.setTimeout(() => {
          if (cancelled) return;
          toast.dismiss(TOAST_ID);

          toast.custom(
            () => (
              <MobileHintToastCard
                onGotIt={async () => {
                  try {
                    await apiClient.dismissMobileHint();
                  } catch {
                    toast.error(
                      "Couldn’t save. Check your connection and try again."
                    );
                    return;
                  }
                  try {
                    localStorage.setItem(dismissedStorageKey(user.uid), "1");
                  } catch {
                    /* ignore */
                  }
                  toast.dismiss(TOAST_ID);
                }}
              />
            ),
            {
              id: TOAST_ID,
              position: "top-center",
              duration: TOAST_DURATION_MS,
              dismissible: true,
            }
          );
        }, 1200);
      } catch {
        // Wrong API / offline / CORS, etc.
      }
    };

    void showHint();

    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [loading, user?.uid]);

  return null;
}
